/**
 * Task Schemas
 *
 * Zod validation schemas for task-related API requests.
 * Renamed from jobs.ts to align with industry-standard terminology.
 */

import { z } from 'zod';
import { fieldSchema } from './listing';
import {
  DEFAULT_PAGE_LIMIT,
  MAX_PAGE_LIMIT,
  MAX_CUSTOM_INSTRUCTIONS_LENGTH,
} from '../constants';

// ============================================================================
// Enums
// ============================================================================

export const taskTypeSchema = z.enum([
  'thumbnail_generation',
  'listing_generation',
  'design_check',
  'field_generation',
  'listing_check',
]);

export const taskStatusSchema = z.enum(['pending', 'processing', 'completed', 'failed', 'cancelled']);

export const taskEntityTypeSchema = z.enum(['design', 'listing', 'batch', 'batch_item']);

// ============================================================================
// API Request Schemas
// ============================================================================

/**
 * GET /tasks query parameters (cursor-based pagination)
 */
export const taskListQuerySchema = z.object({
  status: taskStatusSchema.optional(),
  unseen: z.coerce.boolean().optional().default(false),
  entity_type: taskEntityTypeSchema.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_LIMIT).optional().default(DEFAULT_PAGE_LIMIT),
});

/**
 * POST /tasks/mark-seen request body
 */
export const markSeenRequestSchema = z
  .object({
    task_ids: z.array(z.string().uuid()).optional(),
    all: z.boolean().optional(),
  })
  .refine((data) => data.task_ids !== undefined || data.all === true, {
    message: 'Either task_ids array or all=true must be provided',
  });

/**
 * Task ID parameter validation
 */
export const taskIdParamSchema = z.object({
  taskId: z.string().uuid(),
});

// ============================================================================
// Task Payload Schemas (for queue messages)
// ============================================================================

/**
 * Thumbnail generation task payload
 */
export const thumbnailTaskPayloadSchema = z.object({
  type: z.literal('thumbnail_generation'),
  task_id: z.string().uuid(),
  user_id: z.string(),
  design_id: z.string().uuid(),
  storage_path: z.string(),
  trace_id: z.string().optional(),
});

/**
 * Listing generation task payload
 */
export const listingTaskPayloadSchema = z.object({
  type: z.literal('listing_generation'),
  task_id: z.string().uuid(),
  user_id: z.string(),
  design_id: z.string().uuid(),
  batch_id: z.string().uuid().optional(),
  batch_item_id: z.string().uuid().optional(),
  custom_instructions: z.string().max(MAX_CUSTOM_INSTRUCTIONS_LENGTH).optional(),
  use_fallback: z.boolean().optional(),
  trace_id: z.string().optional(),
});

/**
 * Design check task payload
 */
export const designCheckTaskPayloadSchema = z.object({
  type: z.literal('design_check'),
  task_id: z.string().uuid(),
  user_id: z.string(),
  design_id: z.string().uuid(),
  trace_id: z.string().optional(),
});

/**
 * Field generation task payload
 */
export const fieldGenerationTaskPayloadSchema = z.object({
  type: z.literal('field_generation'),
  task_id: z.string().uuid(),
  user_id: z.string(),
  listing_id: z.string().uuid(),
  design_id: z.string().uuid(),
  field: fieldSchema,
  custom_instructions: z.string().max(MAX_CUSTOM_INSTRUCTIONS_LENGTH).optional(),
  trace_id: z.string().optional(),
});

/**
 * Union schema for all task payloads
 */
export const taskMessageSchema = z.discriminatedUnion('type', [
  thumbnailTaskPayloadSchema,
  listingTaskPayloadSchema,
  designCheckTaskPayloadSchema,
  fieldGenerationTaskPayloadSchema,
]);

// ============================================================================
// Task Result Schemas
// ============================================================================

/**
 * Thumbnail task result
 */
export const thumbnailTaskResultSchema = z.object({
  thumb_sm_path: z.string(),
  thumb_md_path: z.string(),
});

/**
 * Listing task result
 */
export const listingTaskResultSchema = z.object({
  listing_id: z.string().uuid(),
  check_status: z.enum(['approved', 'warning', 'rejected']),
});

/**
 * Design check task result
 */
export const designCheckTaskResultSchema = z.object({
  check_status: z.enum(['approved', 'warning', 'rejected']),
  feedback: z.array(z.string()),
});

/**
 * Field generation task result
 */
export const fieldGenerationTaskResultSchema = z.object({
  field: fieldSchema,
  value: z.string(),
  compliance_status: z.enum(['approved', 'warning', 'rejected']),
});

// ============================================================================
// Type Exports
// ============================================================================

export type TaskListQuery = z.infer<typeof taskListQuerySchema>;
export type MarkSeenRequest = z.infer<typeof markSeenRequestSchema>;
